import { sanitizeHtml } from "@1-now/booking-core";
import { useState } from "react";
import { useData } from "data/data-provider";
import { sectionPaddingY } from "utils/sectionSpacing";
import DbIcon from "designs/DesignB/components/icons";
import { sameTabLinks, useRichLinkClick } from "utils/richLinks";

const RICH =
  "[&_p]:mb-2 last:[&_p]:mb-0 [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1 " +
  "[&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_a]:underline";

/**
 * Design B's FAQ as an accordion (1NOW-11175). Its own component, not FAQ.tsx: Design A's home and
 * Contact pages import that one and must keep drawing it as they did.
 *
 * FAQ as an accordion, whichever layout the FMS picked: plain rows divided by
 * hairlines with a chevron (after booking.com's FAQ), the first one open, one open at a time.
 * Ten open boxes made the page very long; the answers are still in the page for search engines
 * and screen readers.
 */
const FaqAccordion = () => {
  const { faqs } = useData();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  // An answer can link to a page of this site ("See our Terms & Conditions", 1NOW-11114): same
  // tab, inside the app.
  const onLinkClick = useRichLinkClick();

  const items: any[] = faqs?.keypoints ?? [];
  const hasHeader = Boolean(faqs?.heading || faqs?.description);

  if (!hasHeader && items.length === 0) return null;

  return (
    <section
      className={`relative ${sectionPaddingY(faqs?.vertical_spacing, "py-10 md:py-14 lg:py-[76px]")}`}
    >
      {hasHeader && (
        <div className="mb-6 sm:mb-8">
          <div className="db-eyebrow mb-2">FAQ</div>
          {faqs?.heading && (
            <h2
              className="db-title [&_p]:m-0 [&_p]:inline"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(faqs.heading) }}
            />
          )}
          {faqs?.description && (
            <div
              className={`db-sub ${RICH}`}
              onClick={onLinkClick}
              dangerouslySetInnerHTML={{ __html: sameTabLinks(sanitizeHtml(faqs.description)) }}
            />
          )}
        </div>
      )}

      {items.length > 0 && (
        <div className="db-faq">
          {items.map((item: any, index: number) => {
            const isOpen = openIndex === index;
            const panelId = `db-faq-a-${index}`;
            return (
              <div key={index} className={`db-faq__item ${isOpen ? "is-open" : ""}`}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className="db-faq__q"
                >
                  <span
                    className="min-w-0 flex-1 [&_p]:m-0 [&_p]:inline [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.heading ?? "") }}
                  />
                  <DbIcon name="chevronDown" className="db-faq__chev" />
                </button>

                {/* The grid-rows trick animates between collapsed and auto height without
                    measuring; the answer stays in the DOM when closed. */}
                <div
                  id={panelId}
                  role="region"
                  className={`db-faq__a ${isOpen ? "is-open" : ""}`}
                >
                  <div className="overflow-hidden">
                    <div
                      className={`db-faq__body ${RICH}`}
                      onClick={onLinkClick}
                      dangerouslySetInnerHTML={{ __html: sameTabLinks(sanitizeHtml(item?.description ?? "")) }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default FaqAccordion;
